import { Alert, Box, Button, Card, CardContent, Divider, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { gateway } from "../../../gateway";
import { colors } from "../../ui/colors";

const WorkforceCompanyDeletion = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    firstName: "",
    email: "",
    company: "",
    jobsLastMonth: "",
    notes: "",
  });

  const handleChange = (field: keyof typeof form) => (e: any) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    if (!form.company) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        firstName: form.firstName.trim(),
        email: form.email?.trim() || undefined,
        company: form.company.trim(),
        jobsLastMonth: form.jobsLastMonth?.trim() || undefined,
        notes: form.notes?.trim() || undefined,
      };

      await gateway.post("/workforce/request-delete", payload);

      setSubmitted(true);
    } catch (err) {
      setError("We could not process your request at this time. Please try again later.");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "var(--fx-surface-sunken, #F8FAFC)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 2,
      }}
    >
      <Card
        elevation={4}
        sx={{
          width: "100%",
          maxWidth: 500,
          borderRadius: 4,
          padding: "24px 20px",
          textAlign: "center",
        }}
      >
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <img src="https://flownect-media-dev.s3.us-west-1.amazonaws.com/images/flownect-logo.png" alt="Flownect Logo" style={{ width: 110 }} />
          </Box>

          <Typography variant="h5" fontWeight={700} gutterBottom>
            Request Account Deletion
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Submit a request to permanently delete your Flownect account and all personal data associated with it.
          </Typography>

          <Alert severity="warning" sx={{ textAlign: "left", mb: 3 }}>
            <Typography variant="body2">
              • This request permanently deletes your entire Flownect account
              <br />
              • All personal information and related data across all companies will be removed
              <br />
              • Tracking, device, workforce, and mobile app data will be deleted
              <br />
              • Job history may be retained only in an anonymized, non-identifiable form if required for legal or operational reasons
              <br />
              • This action cannot be undone
              <br />• Requests are processed within 30 days
            </Typography>
          </Alert>

          {!submitted ? (
            <Stack spacing={3}>
              <TextField fullWidth required label="First name" value={form.firstName} onChange={handleChange("firstName")} />

              <TextField fullWidth type="email" required label="Email address" value={form.email} onChange={handleChange("email")} />

              <TextField
                fullWidth
                label="Company name (optional)"
                placeholder="Used only to help identify your records"
                value={form.company}
                onChange={handleChange("company")}
              />

              <Divider />

              <Typography variant="subtitle2" sx={{ textAlign: "left", color: "text.secondary" }}>
                Optional verification (helps us identify and remove your account faster)
              </Typography>

              <TextField
                fullWidth
                label="Approx. jobs completed last month"
                placeholder="e.g. 10"
                value={form.jobsLastMonth}
                onChange={handleChange("jobsLastMonth")}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Additional notes (optional)"
                placeholder="Any detail that helps confirm your account ownership"
                value={form.notes}
                onChange={handleChange("notes")}
              />

              {error && <Alert severity="error">{error}</Alert>}

              <Button
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  backgroundColor: colors.orange.base,
                  ":hover": { backgroundColor: colors.orange.base },
                  borderRadius: 2,
                }}
                onClick={handleSubmit}
              >
                {loading ? "Submitting..." : "Submit Account Deletion Request"}
              </Button>

              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                We will contact you only if additional verification is required.
              </Typography>
            </Stack>
          ) : (
            <>
              <Alert severity="success" sx={{ textAlign: "left", mb: 2 }}>
                <Typography variant="body2">
                  Your account deletion request has been received.
                  <br />
                  <br />
                  If an account exists for <strong>{form.email}</strong>, it will be permanently removed along with all associated personal data
                  within the stated timeframe.
                </Typography>
              </Alert>

              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                No further action is required from you.
              </Typography>
            </>
          )}

          <Typography variant="caption" sx={{ mt: 4, display: "block", color: "text.secondary" }}>
            Flownect • Account Deletion, Privacy & Data Protection
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WorkforceCompanyDeletion;
